/**
 * NotificationBell — the header bell shared by web-dispatch and web-billing (Phase 09 / 22,
 * OPEN-085 follow-up). Moved here from web-dispatch's bell so both apps render the same thing.
 *
 * Presentational: the app passes the server rows and the mark-read action (from
 * use-server-notifications.ts, the single read-logic module), plus what only the app knows —
 * how to format a time in the user's timezone (DEC-004 / DEC-066), where a row links to, and
 * an optional app-local section (web-dispatch's "Rejections seen this session").
 *
 *   - "For your role" — unread server rows. Clicking one marks it read and, when the app
 *     gives it a link, closes the drawer and follows the link. Read state is shared by the
 *     whole role, and the drawer says so.
 *   - The app-local section, if any, below it. Its unread count joins the badge, and
 *     "Mark all read" clears it too.
 *
 * aria-label="Notifications" (UI-SPEC "Icon-only controls" requirement).
 *
 * Spec: UI-SPEC §7 (notification bell); Copywriting; DISPATCH-05; T-09-04-06.
 */

import * as React from 'react';
import { Bell } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Button } from '../components/ui/button';
import {
  SHARED_READ_NOTE,
  totalUnread,
  unreadServerNotifications,
  type InAppNotification,
} from './use-server-notifications.js';

/** Where a server row leads: the action label shown on the row, and what clicking does. */
export interface NotificationLink {
  readonly label: string;
  readonly open: () => void;
}

/** Notices the app keeps itself (not on the server), shown under the server rows. */
export interface LocalNotices {
  readonly unread: number;
  readonly isEmpty: boolean;
  readonly markAllRead: () => void;
  /** The rendered section, or null when there is nothing to show. */
  readonly section: React.ReactNode;
}

export interface NotificationBellProps {
  /** GET /v1/notifications rows (useServerNotifications().data?.notifications). */
  readonly rows: readonly InAppNotification[] | undefined;
  /** Marks one row read (useMarkNotificationRead().mutate). */
  readonly onMarkRead: (id: string) => void;
  /** Formats a row's createdAt in the user's own timezone (DEC-004 / DEC-066). */
  readonly formatWhen: (iso: string) => string;
  /** Where a row links to in this app; null (or omitted) means the row only marks read. */
  readonly linkFor?: (n: InAppNotification) => NotificationLink | null;
  readonly local?: LocalNotices;
}

/** Short label for a server notification, from what it points at. */
export function serverNotificationLabel(n: InAppNotification): string {
  if (n.relatedEntityType === 'job') return 'Job alert';
  if (n.relatedEntityType === 'CATALOG_CHANGE') return 'Operator change';
  // OPEN-146 (0231) — Paelode catalog edits: CATALOG_WELL_*, CATALOG_OPERATOR_*, CATALOG_SUMMARY.
  if (n.relatedEntityType?.startsWith('CATALOG_WELL_') === true) return 'Well change';
  if (n.relatedEntityType?.startsWith('CATALOG_OPERATOR_') === true) return 'Operator change';
  if (n.relatedEntityType === 'CATALOG_SUMMARY') return 'Catalog changes';
  return 'Notice';
}

export const NOTIFICATION_SECTION_HEADING_STYLE: React.CSSProperties = {
  margin: 0,
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
};

export const NOTIFICATION_TIME_STYLE: React.CSSProperties = {
  fontSize: '12px',
  fontFamily: 'var(--font-mono, monospace)',
  color: 'var(--color-text-tertiary)',
  whiteSpace: 'nowrap',
};

export function NotificationBell({
  rows,
  onMarkRead,
  formatWhen,
  linkFor,
  local,
}: NotificationBellProps): React.JSX.Element {
  const [sheetOpen, setSheetOpen] = React.useState(false);

  const serverRows = unreadServerNotifications(rows);
  const unreadCount = totalUnread(local?.unread ?? 0, rows);
  const localEmpty = local?.isEmpty ?? true;

  const handleOpen = (): void => {
    setSheetOpen(true);
  };

  const handleMarkAllRead = (): void => {
    local?.markAllRead();
    for (const n of serverRows) onMarkRead(n.id);
  };

  const handleServerClick = (n: InAppNotification, link: NotificationLink | null): void => {
    onMarkRead(n.id);
    if (link !== null) {
      setSheetOpen(false);
      link.open();
    }
  };

  return (
    <>
      {/* Bell button — MUST have aria-label="Notifications" (UI-SPEC accessibility) */}
      <button
        type="button"
        aria-label="Notifications"
        onClick={handleOpen}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          border: 'none',
          borderRadius: '6px',
          backgroundColor: 'transparent',
          color: unreadCount > 0 ? 'var(--color-danger)' : 'var(--color-text-secondary)',
          cursor: 'pointer',
          transition: 'background-color 0.15s ease',
        }}
        onMouseEnter={(e): void => {
          e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
        }}
        onMouseLeave={(e): void => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <Bell size={18} aria-hidden="true" />
        {unreadCount > 0 && (
          <span
            aria-label={`${unreadCount.toString()} unread notification${unreadCount === 1 ? '' : 's'}`}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '16px',
              height: '16px',
              padding: '0 3px',
              borderRadius: '8px',
              backgroundColor: 'var(--color-danger)',
              color: 'var(--color-on-status)',
              fontSize: '10px',
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            {unreadCount > 99 ? '99+' : String(unreadCount)}
          </span>
        )}
      </button>

      {/* Notifications drawer */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent
          side="right"
          style={{
            width: '360px',
            backgroundColor: 'var(--color-bg-secondary)',
            borderLeft: '1px solid var(--color-border-primary)',
            color: 'var(--color-text-primary)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <SheetHeader>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <SheetTitle style={{ color: 'var(--color-text-primary)', fontSize: '16px' }}>
                Notifications
              </SheetTitle>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  onClick={handleMarkAllRead}
                  style={{
                    fontSize: '12px',
                    color: 'var(--color-accent-blue)',
                    padding: '4px 8px',
                  }}
                >
                  Mark all read
                </Button>
              )}
            </div>
          </SheetHeader>

          <div style={{ flex: 1, overflowY: 'auto', padding: '16px 0' }}>
            {serverRows.length === 0 && localEmpty ? (
              <p
                style={{
                  textAlign: 'center',
                  color: 'var(--color-text-tertiary)',
                  fontSize: '14px',
                  padding: '32px 0',
                }}
              >
                No notifications
              </p>
            ) : (
              <>
                {serverRows.length > 0 && (
                  <section aria-label="For your role">
                    <h3 style={NOTIFICATION_SECTION_HEADING_STYLE}>For your role</h3>
                    <p
                      style={{
                        margin: 0,
                        padding: '0 16px 8px',
                        fontSize: '12px',
                        color: 'var(--color-text-tertiary)',
                      }}
                    >
                      {SHARED_READ_NOTE}
                    </p>
                    {serverRows.map((n) => {
                      const link = linkFor?.(n) ?? null;
                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={(): void => {
                            handleServerClick(n, link);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '12px 16px',
                            border: 'none',
                            borderBottom: '1px solid var(--color-border-primary)',
                            backgroundColor: 'transparent',
                            color: 'inherit',
                            cursor: 'pointer',
                          }}
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: '8px',
                            }}
                          >
                            <span
                              style={{
                                fontSize: '12px',
                                fontWeight: 600,
                                color: 'var(--color-link)',
                              }}
                            >
                              {serverNotificationLabel(n)}
                            </span>
                            <span style={NOTIFICATION_TIME_STYLE}>{formatWhen(n.createdAt)}</span>
                          </span>
                          <span
                            style={{
                              display: 'block',
                              margin: '4px 0 0',
                              fontSize: '14px',
                              color: 'var(--color-text-primary)',
                              lineHeight: 1.5,
                            }}
                          >
                            {n.message}
                          </span>
                          <span
                            style={{
                              display: 'block',
                              margin: '4px 0 0',
                              fontSize: '12px',
                              color: 'var(--color-accent-blue)',
                            }}
                          >
                            {link?.label ?? 'Mark read'}
                          </span>
                        </button>
                      );
                    })}
                  </section>
                )}

                {!localEmpty && local?.section}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
